import { toast } from 'sonner'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { api } from '@/lib/api'
import { StatusBadge } from '@/components/shared/StatusBadge'
import { EmptyState } from '@/components/shared/EmptyState'
import type { Domain } from '@/types'
import { Globe, Plus, Trash2, CheckCircle2, XCircle, Loader2, ChevronRight, ChevronLeft, Wand2 } from 'lucide-react'
import { DomainSetupWizard } from '@/components/shared/DomainSetupWizard'

export function Domains() {
  const navigate = useNavigate()
  const [domains, setDomains] = useState<Domain[]>([])
  const [loading, setLoading] = useState(true)
  const [page, setPage] = useState(1)
  const [total, setTotal] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [addOpen, setAddOpen] = useState(false)
  const [newDomain, setNewDomain] = useState('')
  const [domainError, setDomainError] = useState('')
  const [adding, setAdding] = useState(false)
  const [wizardDomain, setWizardDomain] = useState<Domain | null>(null)
  const [wizardOpen, setWizardOpen] = useState(false)

  // CHECKPOINT: PRD-10 FR-10.3.3 — Domain format validation
  const DOMAIN_REGEX = /^(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z0-9][a-z0-9-]{0,61}[a-z0-9]$/

  const validateDomain = (value: string) => {
    const trimmed = value.trim().toLowerCase()
    if (!trimmed) {
      setDomainError('')
      return false
    }
    if (trimmed.length > 253) {
      setDomainError('Domain name is too long.')
      return false
    }
    if (!DOMAIN_REGEX.test(trimmed)) {
      setDomainError('Enter a valid domain name (e.g. example.com).')
      return false
    }
    setDomainError('')
    return true
  }

  const fetchDomains = async () => {
    try {
      const res = await api.get(`/domains?page=${page}&per_page=20`)
      setDomains(res.data.domains || [])
      setTotal(res.data.pagination?.total || 0)
      setTotalPages(res.data.pagination?.total_pages || 1)
    } catch {
      toast.error('Failed to load domains')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchDomains()
  }, [page])

  const handleAdd = async () => {
    const name = newDomain.trim().toLowerCase()
    if (!validateDomain(name)) return
    setAdding(true)
    try {
      const res = await api.post('/domains', { name })
      const created = res.data as Domain
      setWizardDomain(created)
      setWizardOpen(true)
      setNewDomain('')
      setDomainError('')
      fetchDomains()
    } catch (err: any) {
      // CHECKPOINT: PRD-10 FR-10.13.5 — Use toast for async operation errors
      const detail = err.response?.data?.detail
      if (detail?.error !== 'SUBSCRIPTION_REQUIRED') {
        toast.error(detail?.message || detail || 'Failed to add domain')
      }
    } finally {
      setAdding(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this domain? It will enter a 30-day grace period.')) return
    try {
      await api.delete(`/domains/${id}`)
      fetchDomains()
    } catch {
      toast.error('Failed to delete domain')
    }
  }

  if (loading) {
    return (
      <div className="space-y-4 animate-pulse">
        <div className="h-8 w-32 bg-accent rounded" />
        <div className="h-64 bg-accent rounded-xl" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Domains</h1>
          <p className="text-sm text-muted-foreground mt-1">Manage your email domains</p>
        </div>
        <Button onClick={() => setAddOpen(true)} aria-label="Add new domain" className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700">
          <Plus className="mr-2 h-4 w-4" />
          Add Domain
        </Button>
      </div>

      {domains.length === 0 ? (
        <EmptyState
          title="No domains yet"
          description="Add your first domain to start sending and receiving email."
          icon={<Globe className="h-5 w-5" />}
          action={<Button onClick={() => setAddOpen(true)}><Plus className="mr-2 h-4 w-4" />Add Domain</Button>}
        />
      ) : (
        <div className="grid gap-3">
          {domains.map((domain) => (
            <Card
              key={domain.id}
              className="hover:bg-accent/20 transition-colors cursor-pointer"
              onClick={() => navigate(`/domains/${domain.id}`)}
            >
              <CardContent className="p-4 flex items-center justify-between">
                <div className="flex items-center gap-4 min-w-0">
                  <div className="h-10 w-10 rounded-lg bg-accent flex items-center justify-center shrink-0">
                    <Globe className="h-5 w-5 text-violet-400" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-semibold truncate">{domain.name}</p>
                      <StatusBadge status={domain.status} />
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      <DNSDot label="MX" ok={domain.dns_health?.mx?.status === 'verified'} />
                      <DNSDot label="SPF" ok={domain.dns_health?.spf?.status === 'verified'} />
                      <DNSDot label="DKIM" ok={domain.dns_health?.dkim?.status === 'verified'} />
                      <DNSDot label="DMARC" ok={domain.dns_health?.dmarc?.status === 'verified'} />
                      <span className="text-xs text-muted-foreground">
                        {domain.mailbox_count} mailboxes
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {domain.quota_used_mb} MB / {domain.quota_mb} MB
                      </span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {domain.status === 'pending' && (
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => { e.stopPropagation(); setWizardDomain(domain); setWizardOpen(true); }}
                    >
                      <Wand2 className="mr-1.5 h-3.5 w-3.5" />
                      Set up
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive"
                    onClick={(e) => { e.stopPropagation(); handleDelete(domain.id); }}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {domains.length > 0 && (
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground">{total} total</p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={page >= totalPages}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Add Domain Dialog */}
      <Dialog open={addOpen} onOpenChange={(open) => { setAddOpen(open); if (!open) { setNewDomain(''); setDomainError('') } }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Domain</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 py-2">
            {/* CHECKPOINT: PRD-10 FR-10.3.3 — Domain name input with live validation */}
            <label htmlFor="domain-name" className="text-sm font-medium">Domain Name</label>
            <Input
              id="domain-name"
              placeholder="example.com"
              value={newDomain}
              onChange={(e) => {
                setNewDomain(e.target.value)
                validateDomain(e.target.value)
              }}
              onKeyDown={(e) => e.key === 'Enter' && handleAdd()}
              aria-invalid={!!domainError}
              aria-describedby="domain-error"
            />
            {domainError ? (
              <p id="domain-error" className="text-xs text-destructive">{domainError}</p>
            ) : (
              <p className="text-xs text-muted-foreground">
                You must own this domain and be able to change its DNS records.
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Cancel</Button>
            <Button onClick={handleAdd} disabled={adding || !newDomain || !!domainError}>
              {adding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Add Domain
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {wizardDomain && (
        <DomainSetupWizard
          domain={wizardDomain}
          open={wizardOpen}
          onClose={() => { setWizardOpen(false); setWizardDomain(null) }}
          onUpdated={fetchDomains}
        />
      )}
    </div>
  )
}

function DNSDot({ label, ok }: { label: string; ok?: boolean }) {
  return (
    <div className="flex items-center gap-1" title={label}>
      {ok ? (
        <CheckCircle2 className="h-3 w-3 text-emerald-400" />
      ) : (
        <XCircle className="h-3 w-3 text-slate-500" />
      )}
      <span className="text-[10px] text-muted-foreground uppercase">{label}</span>
    </div>
  )
}
